import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AgentConfig, QueryType, PersonaConfig, CallEventAdapter, OrderStatus } from "../types";
import { useSession } from "../context/SessionContext";
import { useCallEvents } from "../hooks/useCallEvents";
import { useCallTimer } from "../hooks/useCallTimer";
import { NuplayCallAdapter, agentIdForQuery } from "../adapters/nurixAdapter";
import { fetchCustomerOrders, shopifyConfigured } from "../shopify/client";
import { shopifyCustomerIdFromPersonaId } from "../lib/shopifyLookup";
import { PostCallAnalytics } from "../components/PostCallAnalytics";
import { getVertical } from "../data/verticals";
import { verticalPlaybook, playbookFor } from "../data/playbooks";
import { getTscCustomer, TSC_CUSTOMERS, TSC_PERSONA_ID_PREFIX } from "../data/tscCustomers";
import { tscLiveHint } from "../tsc/tscHints";
import { TSC_CONSOLE_CSS, TSC_LOGO_SRC } from "../tsc/tscConsoleSkin";
import { TscDemo, isTscSlug, tscDemoForSlug } from "../tsc/tscDemos";
import { TscResetDemo } from "../tsc/TscResetDemo";
import { TscAgentToggle } from "../tsc/TscChrome";

// ─────────────────────────────────────────────────────────────────────────────
// NuPlay Electronics call experience — three-column layout (imported design):
//   left  · scenario nav + agent capabilities
//   center· call stage (Ready → Start call → live transcript)
//   right · customer context · orders · live workflow · activity
// Fonts use Geist — the rest of the site's font (loaded globally), not the design's Nunito.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  agent: AgentConfig;
  queryType: QueryType;
  persona: PersonaConfig;
}

const ORDER_STATUS_TEXT: Record<OrderStatus, string> = {
  delivered: "Delivered", delayed: "Delayed", in_transit: "In transit",
  out_for_delivery: "Out for delivery", cancelled: "Cancelled", exception: "Exception",
  returned: "Returned", label_created: "Label created", ready_for_pickup: "Ready for pickup",
  pickup_processing: "Pickup processing",
};

function humanizeFulfillment(s: string | null): string {
  if (!s) return "Unfulfilled";
  return s.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

const usd = (n: number) => `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const LTV_LABEL: Record<string, string> = { vip: "VIP", high: "High", mid: "Mid", low: "Low" };

// ── Minimal line icons ───────────────────────────────────────────────────────
const sv = { fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
const Ic = ({ d, s = 16, fill = false }: { d: string; s?: number; fill?: boolean }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" {...(fill ? { fill: "currentColor" } : sv)}>
    {d.split("|").map((p, i) => <path key={i} d={p} />)}
  </svg>
);
const IcBox = () => <Ic d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z|M3.27 6.96 12 12.01l8.73-5.05|M12 22.08V12" />;
const IcCheck = () => <Ic d="M9 11l3 3L22 4|M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />;
const IcMic = ({ s = 22 }: { s?: number }) => <Ic s={s} d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z|M19 11a7 7 0 0 1-14 0|M12 18v3" />;
const IcStore = () => <Ic d="M3 9l1.4-5h15.2L21 9|M5 9v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9|M9 20v-6h6v6" />;
const IcTag = () => <Ic d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z|M7 7h.01" />;
const IcX = ({ s = 16 }: { s?: number }) => <Ic s={s} d="M18 6 6 18|M6 6l12 12" />;
const IcChat = () => <Ic s={15} d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />;
const IcActivity = () => <Ic s={15} d="M22 12h-4l-3 8L9 4l-3 8H2" />;
const IcSpark = () => <Ic s={14} fill d="M12 2l1.5 5.2L18.5 9l-5 1.8L12 16l-1.5-5.2L5.5 9l5-1.8z" />;
const IcMail = () => <Ic d="M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z|M3 7l9 6 9-6" />;
const IcPhoneSm = () => <Ic s={15} d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.07 12 19.79 19.79 0 0 1 1 3.38 2 2 0 0 1 3 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L7.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />;
const IcPin = () => <Ic d="M12 21s-7-5.5-7-11a7 7 0 0 1 14 0c0 5.5-7 11-7 11z|M12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z" />;
const IcCheckmark = ({ s = 14 }: { s?: number }) => <Ic s={s} d="M20 6L9 17l-5-5" />;
const IcHelp = ({ s = 16 }: { s?: number }) => <Ic s={s} d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z|M9.6 9a2.5 2.5 0 0 1 4.8 1c0 1.7-2.4 2-2.4 3.5|M12 17h.01" />;

type Tone = "green" | "amber" | "red" | "grey";
function statusTone(s: OrderStatus): Tone {
  if (s === "delivered") return "green";
  if (s === "in_transit" || s === "out_for_delivery" || s === "delayed" || s === "ready_for_pickup") return "amber";
  if (s === "exception") return "red";
  return "grey";
}
function fulfillTone(s: string | null): Tone {
  const t = (s || "").toUpperCase();
  if (t === "FULFILLED") return "green";
  if (t.includes("PARTIAL") || t.includes("PROGRESS")) return "amber";
  return "grey";
}

function validatePhone(value: string): string | null {
  const digits = value.replace(/\D/g, "");
  if (digits.length < 7) return "Enter a valid phone number";
  if (["1234567890", "0000000000", "1111111111"].some((p) => digits.includes(p))) return "Enter a real phone number";
  return null;
}

// The TSC "your email" box. A demo customer's (made-up) email is refused: the crew would ignore it anyway and send
// to the Nurix inboxes.
function validateTesterEmail(value: string): string | null {
  if (!/^[^\s@{}<>,;:"'()[\]\\]+@[^\s@{}<>,;:"'()[\]\\]+\.[^\s@{}<>,;:"'()[\]\\]{2,}$/.test(value)) return "Enter a full email address, like name@company.com";
  if (TSC_CUSTOMERS.some((c) => c.email.toLowerCase() === value)) return "That's a demo customer's email. Enter your own";
  return null;
}

// Agent avatar — uses agent.avatarUrl (falls back to a sparkle glyph, or to `initial` in white
// on the brand colour when set: the TSC agents, e.g. Mike until his photo exists). The error is
// per src, so switching Mike back to Riley shows Riley's photo again.
function AgentAvatar({ src, initial }: { src?: string; initial?: string }) {
  const [errSrc, setErrSrc] = useState<string | null>(null);
  if (!src || errSrc === src) return initial ? <span className="ex-voice-initial">{initial}</span> : <IcSpark />;
  return <img className="ex-voice-img" src={src} alt="" onError={() => setErrSrc(src)} />;
}

// Customer avatar — the persona's portrait when it has one, else its initials.
function PersonaAvatar({ src, initials }: { src?: string; initials: string }) {
  const [imgErr, setImgErr] = useState(false);
  if (imgErr || !src) return <>{initials}</>;
  return <img className="ex-person-img" src={src} alt="" onError={() => setImgErr(true)} />;
}

// Live Workflow middle node: the specialist the call is currently routed to,
// detected live from the caller's words + the agent's tool calls.
type SpecId = "tracking" | "store" | "cancel" | "price" | "product";
const SPECIALISTS: Record<SpecId, { label: string; Icon: () => JSX.Element; text: RegExp; tool: RegExp }> = {
  tracking: {
    label: "Order Tracking", Icon: IcBox,
    text: /\b(where('?s| is)|track|tracking|deliver\w*|arriv\w*|shipp?\w*|dispatch\w*|package|parcel|courier|on its way|order status)\b/i,
    tool: /track|shipment|get_orders?\b|fulfil/i,
  },
  store: {
    label: "Store Pickup", Icon: IcStore,
    text: /\b(pick ?up|in[- ]?store|store|collect|nearest|location|in stock|branch|outlet)\b/i,
    tool: /inventory|store|location|pickup/i,
  },
  cancel: {
    label: "Returns & Refund", Icon: IcCheck,
    text: /\b(cancel\w*|refund\w*|return\w*|money back|send (it )?back|reimburs\w*)\b/i,
    tool: /cancel|refund|return/i,
  },
  price: {
    label: "Price Objection", Icon: IcTag,
    text: /\b(too expensive|expensive|cheaper|cheapest|discount|coupon|promo|price match|lower (the )?price|better price|can'?t afford|cost\w* too much|out of (my )?budget|any deal|bring.*down)\b/i,
    tool: /price|discount|coupon|promo|match/i,
  },
  product: {
    label: "Product & Pricing", Icon: IcSpark,
    text: /\b(recommend\w*|suggest\w*|which (one|model)|what.*best|compare|looking for|good (for|gift)|specs?|features?)\b/i,
    tool: /recommend|product|catalog/i,
  },
};
const SPEC_PRIORITY: SpecId[] = ["price", "cancel", "store", "tracking", "product"];
const QUERY_DEFAULT: Partial<Record<QueryType, SpecId>> = {
  "wismo": "tracking", "cancellation": "cancel", "store-desk": "store", "product-rec": "product",
};
function detectSpecialist(
  transcript: { speaker: string; text: string }[],
  liveActions: { type: string; functionName?: string }[],
  queryType: QueryType,
): SpecId {
  for (let i = transcript.length - 1; i >= 0; i--) {
    const t = transcript[i];
    if (t.speaker !== "user") continue;
    const hit = SPEC_PRIORITY.find((id) => SPECIALISTS[id].text.test(t.text));
    if (hit) return hit;
  }
  for (let i = liveActions.length - 1; i >= 0; i--) {
    const a = liveActions[i];
    if (a.type !== "tool_call" || !a.functionName) continue;
    const hit = SPEC_PRIORITY.find((id) => SPECIALISTS[id].tool.test(a.functionName!));
    if (hit) return hit;
  }
  return QUERY_DEFAULT[queryType] ?? "tracking";
}

function humanizeFn(name: string): string {
  const map: Record<string, string> = {
    get_customer: "Look up customer", get_order: "Look up order", get_orders: "Look up orders",
    track_order: "Track shipment", track_shipment: "Track shipment", get_shipment: "Check shipment",
    cancel_order: "Process cancellation", process_cancellation: "Process cancellation",
    initiate_return: "Initiate return", refund_order: "Process refund",
    check_inventory: "Check store availability", get_inventory: "Check store availability",
    get_store: "Find nearest store", recommend_products: "Recommend products",
    get_products: "Browse products", transfer_tool: "Transfer to specialist",
    ftn_lookup: "Look up the account", ftn_log_case: "Log the case",
    tsc_cx_verify_customer: "Verify the caller", tsc_cx_verify_email: "Verify the email",
    tsc_cx_get_order: "Look up order", tsc_cx_check_store: "Ask the store", tsc_cx_cancel_order: "Cancel the order",
    tsc_cx_log_action: "Log the outcome", tsc_cx_recommend: "Find a cheaper pick", tsc_cx_catalog: "Search the catalog",
    tsc_cx_payment_create: "Email a payment link", tsc_cx_payment_status: "Check the payment",
    tsc_cx_send_password_reset: "Send the reset email", tsc_cx_password_reset_status: "Check the password reset",
    tsc_cx_reset_email: "Send the reset email",
    Transfer_Tool: "Transfer to a teammate", end_conversation: "End the call",
  };
  if (map[name]) return map[name];
  return (name || "Tool call")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\b(\w+)\s+\1\b/gi, "$1") // collapse duplicate words: "Get Get Order" -> "Get Order"
    .trim();
}

function shortVal(v: unknown): string {
  if (v == null) return "—";
  if (typeof v === "string") return v;
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    const first = Object.entries(o)[0];
    return first ? `${first[0]}: ${String(first[1])}` : JSON.stringify(o).slice(0, 40);
  }
  return String(v);
}

// Render a tool-call payload (raw args JSON) as readable "Label: value" pairs
// instead of dumping JSON. Humanizes keys, flattens nested objects/arrays, and
// parses stringified-JSON values.
function humanizeKey(k: string): string {
  return k.replace(/[_-]+/g, " ").replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/\b\w/g, (c) => c.toUpperCase()).trim();
}
function readableVal(v: unknown): string {
  if (v == null || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") return String(v);
  if (typeof v === "string") {
    const s = v.trim();
    const gid = s.match(/^gid:\/\/[^/]+\/[A-Za-z]+\/(\d+)/); // gid://shopify/Order/123 -> #123
    if (gid) return `#${gid[1]}`;
    const iso = s.match(/^(\d{4}-\d{2}-\d{2})T[\d:.]+/); // 2026-05-14T05:33:46Z -> 2026-05-14
    if (iso) return iso[1];
    if ((s.startsWith("{") && s.endsWith("}")) || (s.startsWith("[") && s.endsWith("]"))) {
      try { return readableVal(JSON.parse(s)); } catch { /* not JSON, use as-is */ }
    }
    return v;
  }
  if (Array.isArray(v)) return v.map(readableVal).join(", ");
  if (typeof v === "object") return Object.entries(v as Record<string, unknown>).map(([k, val]) => `${humanizeKey(k)}: ${readableVal(val)}`).join(", ");
  return String(v);
}
const PAYLOAD_NOISE = new Set(["query", "headers", "operationname", "extensions", "__typename", "typename", "authorization", "apikey", "token", "type", "cursor", "pageinfo", "hasnextpage", "haspreviouspage", "first", "after", "last", "before"]);
const VAR_LABELS: Record<string, string> = {
  q: "Query", sq: "Query", id: "ID", term: "Search", search: "Search", keyword: "Search",
  title: "Product", displayfulfillmentstatus: "Status", fulfillmentstatus: "Status",
  displayfinancialstatus: "Payment", financialstatus: "Payment", trackingnumber: "Tracking",
};
function looksGraphQL(v: unknown): boolean {
  return typeof v === "string" && /(^|\s)(query|mutation|subscription)\s*[({]/i.test(v) && v.includes("{") && v.includes("}");
}
// Pull the meaningful values out of a tool payload (often a GraphQL request):
// surface the query variables / ids, and skip the raw query string, headers, and blanks.
function collectPayload(obj: unknown, out: { k: string; v: string }[], depth = 0): void {
  if (obj == null || depth > 9) return;
  if (typeof obj === "string") {
    if (looksGraphQL(obj)) return;
    const s = obj.trim();
    if ((s.startsWith("{") && s.endsWith("}")) || (s.startsWith("[") && s.endsWith("]"))) {
      try { collectPayload(JSON.parse(s), out, depth + 1); } catch { /* not JSON */ }
    }
    return;
  }
  if (Array.isArray(obj)) { obj.forEach((x) => collectPayload(x, out, depth + 1)); return; }
  if (typeof obj !== "object") return;
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    const kl = k.toLowerCase();
    if (PAYLOAD_NOISE.has(kl) || looksGraphQL(v)) continue;
    if (v == null || v === "") continue;
    if (typeof v === "object") { collectPayload(v, out, depth + 1); continue; } // unwrap payload / variables / body
    if (typeof v === "string") {
      const s = v.trim(); // MCP wrappers carry the real result as a JSON string (e.g. { type:"text", text:"{...}" })
      if ((s.startsWith("{") && s.endsWith("}")) || (s.startsWith("[") && s.endsWith("]"))) {
        try { collectPayload(JSON.parse(s), out, depth + 1); continue; } catch { /* not JSON */ }
      }
    }
    const label = VAR_LABELS[kl] ?? humanizeKey(k);
    const raw = readableVal(v);
    const value = raw.length > 90 ? `${raw.slice(0, 90).replace(/\s+\S*$/, "").trimEnd()}…` : raw; // keep bullets compact
    if (!out.some((p) => p.k === label && p.v === value)) out.push({ k: label, v: value });
  }
}
function formatPayload(args: unknown): { k: string; v: string }[] {
  const out: { k: string; v: string }[] = [];
  collectPayload(args, out);
  return out.slice(0, 4); // keep it glanceable for a client
}

// ── Static screen data ────────────────────────────────────────────────────────
const CAPABILITIES = [
  { t: "Track orders & shipments", d: "Real-time status, ETA, carrier" },
  { t: "Process returns & refunds", d: "Eligibility check + instant refund" },
  { t: "Warm handoff to a human", d: "Transfers with full context" },
];

// NuPlay console — deep-links into the real `zero` console (US), routes from the repo.
const ZERO_BASE = "https://zero-us.nurixlabs.tech";

// Which console entity this demo drives. Crews carry per-member name overrides;
// single-agent demos (knitwell, fintech) do not, and address a different route.
type ConsoleCtx = { agentId?: string; isCrew: boolean };

// `route` is the plain section landing. `deep` upgrades it to the page for THIS
// demo's crew/agent, and falls back to `route` when the id is unknown.
type NavItem = { label: string; route: string; d: string; active?: boolean; deep?: (c: ConsoleCtx) => string };

const NAV: { group: string; items: NavItem[] }[] = [
  { group: "Build", items: [
    { label: "Agents",    route: "/agents",    d: "M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2|M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
      deep: ({ agentId, isCrew }) => (agentId && !isCrew ? `/agent/voice/${agentId}/build` : "/agents") },
    { label: "Crews",     route: "/crews-v2",  d: "M17 21v-2a4 4 0 0 0-3-3.87|M9 21v-2a4 4 0 0 1 3-3.87|M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z|M23 21v-2a4 4 0 0 0-3-3.87|M16 3.13a4 4 0 0 1 0 7.75",
      deep: ({ agentId, isCrew }) => (agentId && isCrew ? `/crew-v2/${agentId}/build` : "/crews-v2") },
    { label: "Workflows", route: "/workflows", d: "M6 3a2 2 0 1 0 0 4 2 2 0 0 0 0-4z|M18 3a2 2 0 1 0 0 4 2 2 0 0 0 0-4z|M12 17a2 2 0 1 0 0 4 2 2 0 0 0 0-4z|M6 7v2a3 3 0 0 0 3 3h6a3 3 0 0 0 3-3V7|M12 12v5" },
  ]},
  { group: "Manage", items: [
    { label: "Knowledge",    route: "/knowledgebases", d: "M3 4.5A1.5 1.5 0 0 1 4.5 3H11v18H4.5A1.5 1.5 0 0 1 3 19.5z|M13 3h6.5A1.5 1.5 0 0 1 21 4.5v15a1.5 1.5 0 0 1-1.5 1.5H13z" },
    { label: "Integrations", route: "/connections",    d: "M3 3h7v7H3z|M14 3h7v7h-7z|M14 14h7v7h-7z|M3 14h7v7H3z" },
    { label: "Telephony",    route: "/sip-trunk",       d: "M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.07 12 19.79 19.79 0 0 1 1 3.38 2 2 0 0 1 3 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L7.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" },
    { label: "Channels",     route: "/channels",        d: "M3 4h18a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z|M8 20h8|M12 16v4" },
    { label: "Campaigns",    route: "/campaigns",       d: "M3 11v2a1 1 0 0 0 1 1h2l4 4V6L6 10H4a1 1 0 0 0-1 1z|M14 8a4 4 0 0 1 0 8" },
    { label: "Campaign v2",  route: "/campaign-v2",     d: "M3 11v2a1 1 0 0 0 1 1h2l4 4V6L6 10H4a1 1 0 0 0-1 1z|M14 8a4 4 0 0 1 0 8" },
    { label: "A/B Testing",  route: "/experiments",     d: "M9 3h6|M10 3v6l-5 9a2 2 0 0 0 2 3h10a2 2 0 0 0 2-3l-5-9V3|M7 14h10" },
  ]},
  { group: "Monitor", items: [
    { label: "Conversations",      route: "/logs",               d: "M21 11.5a8.38 8.38 0 0 1-9 8.4 9.9 9.9 0 0 1-4-.9L3 21l1.9-4.5a8.38 8.38 0 0 1-.9-4A8.5 8.5 0 0 1 12 4a8.38 8.38 0 0 1 9 7.5z",
      // Calls are logged against the crew, never its members, so an unfiltered
      // /logs lands on whatever agent the console last had selected.
      deep: ({ agentId, isCrew }) => (agentId
        ? `/logs?logs_type=${isCrew ? "crew" : "agent"}&agentId=${agentId}&view=details&date_range_preset=last_7_days`
        : "/logs") },
    { label: "External Analytics", route: "/external-analytics", d: "M3 3v18h18|M7 14l3-3 3 3 5-5|M17 6h4v4" },
    { label: "Analytics",          route: "/analytics",          d: "M3 21h18|M7 21V10|M12 21V4|M17 21v-8" },
    { label: "Quality Check",      route: "/qc",                 d: "M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2|M9 4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2H9z|M9 14l2 2 4-4" },
    { label: "Experience Studio",  route: "/experience",         d: "M4 4h16v16H4z|M12 8l1.2 2.8L16 12l-2.8 1.2L12 16l-1.2-2.8L8 12l2.8-1.2z", active: true },
    { label: "Settings",           route: "/settings",           d: "M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z|M19 12a7 7 0 0 0-.1-1l2-1.5-2-3.5-2.4 1a7 7 0 0 0-1.7-1L14.5 2h-5l-.3 2.5a7 7 0 0 0-1.7 1l-2.4-1-2 3.5 2 1.5a7 7 0 0 0 0 2l-2 1.5 2 3.5 2.4-1a7 7 0 0 0 1.7 1l.3 2.5h5l.3-2.5a7 7 0 0 0 1.7-1l2.4 1 2-3.5-2-1.5a7 7 0 0 0 .1-1z" },
  ]},
];
const OPENERS: Partial<Record<QueryType, string[]>> = {
  "wismo": ["Where's my order?", "My package is late", "Track order {ORDER}"],
  "cancellation": ["I want to cancel my order", "It's too expensive for me", "Cancel order {ORDER}"],
  "store-desk": ["Is my order ready to pick up?", "How long will you hold it?", "Which store is it at?"],
  "product-rec": ["I found it cheaper elsewhere", "Why is shipping so expensive?", "What do you recommend?"],
  "refund-status": ["Where's my refund?", "Has my refund gone through yet?", "I returned something — where's my money?"],
};
// Turn-by-turn prompts shown live during the call so the demo driver knows what to say next.
const CALL_FLOW_HINTS: Partial<Record<QueryType, string[]>> = {
  "wismo": ["Where's my order {ORDER}?", "It hasn't arrived yet — can you check the status?", "When will it get here?", "Can I speak to a human about this?"],
  "cancellation": ["I'd like to cancel order {ORDER}.", "Honestly, it's just too expensive.", "Okay, I'll take that one instead.", "I'll pick it up from the store."],
  "store-desk": ["Is my order ready for pickup?", "How long will you hold it?", "What's the store's phone number?", "Perfect, that's all."],
  "product-rec": ["I found it cheaper somewhere else.", "Why is shipping so expensive?", "What can you do on the price?", "Okay, sign me up."],
  "refund-status": ["I returned an item — where's my refund?", "Has it gone back to my card yet?", "Actually, I might need to reorder.", "Yeah, ship it to me."],
  // Fintech (Claire). These callers came off the chat portal, so the opener states the
  // problem rather than asking a question — and never mentions an order number, which is
  // a retail idea that does not exist on a B2B payments account.
  "returned-payment": ["My bank says the payment went through, but my distributor won't deliver tomorrow.", "So will I actually get my delivery?", "Can you take the returned payment fee off?", "Okay — what happens next?"],
  "compliance-hold": ["My distributor refused this morning's delivery — they say I'm on a state list.", "But I paid this weeks ago.", "Just waive the fee, I've been a customer for years.", "So when does the hold come off?"],
  "missing-invoice": ["I signed for a delivery four days ago and it's still not in my portal.", "I don't want it going past my terms.", "Can someone call me back today?", "Okay, that works."],
  "integration-sync": ["Our invoice files haven't reached our accounting system for three days.", "We close the month on Friday.", "Can someone own this today?", "Alright, thank you."],
};
// Contextual reply suggestions keyed off what the agent JUST said (falls back to the turn-based flow hint).
const AGENT_CUE_HINTS: { re: RegExp; hint: string }[] = [
  { re: /order (number|id|#)|which order|confirm .*order|reference (number|code)|what'?s the order/i, hint: "It's order {ORDER}." },
  { re: /verify|confirm your (name|identity|details)|your (full |last )?name|email( address)?|on file|date of birth/i, hint: "Sure — it's {NAME}." },
  { re: /which (one|would you like)|would you like me to|shall i\b|do you want me to|set (it|that|one) aside|which would you prefer/i, hint: "Yes, go ahead with that." },
  { re: /hold on|one moment|let me (check|pull|look|see)|bear with|give me a (sec|moment)|checking (that|this)/i, hint: "Sure, take your time." },
  { re: /arriv|deliver|on its way|out for delivery|expected|\beta\b|by (tomorrow|today|monday|tuesday|wednesday|thursday|friday|the weekend)/i, hint: "When exactly will it arrive?" },
  { re: /refund|return|reimburse|store credit|voucher/i, hint: "How long will that take?" },
  { re: /pick.?up|in.?store|store hours|nearest store|ready (for|to) (collect|pick)/i, hint: "Which store, and what are the hours?" },
  { re: /sorry|apolog|understand your frustration|i know this is|i realise|i realize/i, hint: "I appreciate that — what can you do?" },
  { re: /anything else|is there anything|something else|all set|is that all/i, hint: "Actually, can I speak to a human?" },
  { re: /how can i help|what can i (do|help)|calling (about|regarding)|reason for your call|how may i/i, hint: "{OPENER}" },
];
const TOUR = [
  { k: "Start the conversation", b: "Begin a web call, or have the agent ring your phone. Add a number to demo a warm transfer to a human." },
  { k: "Not sure what to say?", b: "Tap an opener under Try asking, or hit Start call. Call hints below give you the demo's reference data." },
  { k: "Watch the agent work", b: "Customer context up top, then the live workflow and every tool call as the conversation unfolds." },
];

export function CallSurfaceStep({ agent, queryType, persona }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, dispatch } = useSession();
  // Feeds the left-rail console links so each one opens this demo's own page.
  const consoleCtx: ConsoleCtx = { agentId: agent.agentId, isCrew: !!agent.agentNameOverrides };
  const adapterRef = useRef<CallEventAdapter | null>(null);
  const [callId] = useState(() => `call-${Date.now()}`);
  const [adapterInstance, setAdapterInstance] = useState<CallEventAdapter | null>(null);
  const [waveTick, setWaveTick] = useState(0);
  const [scenario] = useState<QueryType>(queryType || "wismo"); // fixed from the URL/picker; tab switcher removed
  const [channel, setChannel] = useState<"web" | "phone">("web");
  const [dialCode, setDialCode] = useState("+91");
  const [phoneNum, setPhoneNum] = useState("");
  const [transferDialCode, setTransferDialCode] = useState("+91");
  const [transferNum, setTransferNum] = useState("");
  const [showTransfer, setShowTransfer] = useState(false);
  const [testerEmail, setTesterEmail] = useState("");
  const [showEmail, setShowEmail] = useState(false);
  const tscDemo = isTscSlug(agent.slug) ? tscDemoForSlug(agent.slug) : undefined;
  const emailBox = !!tscDemo?.testerEmail;
  const [setupError, setSetupError] = useState<string | null>(null);
  const [sideCollapsed, setSideCollapsed] = useState(false);
  const [tourStep, setTourStep] = useState<number>(() => {
    try { return localStorage.getItem("nx-exp-tour") ? -1 : 0; } catch { return 0; }
  });

  const timerDisplay = useCallTimer(state.startedAt, state.endedAt, state.callState === "in_call");

  const startAdapter = useCallback((adapter: CallEventAdapter) => {
    adapterRef.current = adapter;
    setAdapterInstance(adapter);
    dispatch({ type: "CALL_STATE_CHANGE", state: "setting_up" });
  }, [dispatch]);

  // Disconnect on unmount / pagehide. The call only connects on Start.
  useEffect(() => {
    const endNow = () => { adapterRef.current?.disconnect(); adapterRef.current = null; };
    window.addEventListener("pagehide", endNow);
    return () => {
      window.removeEventListener("pagehide", endNow);
      adapterRef.current?.disconnect();
      adapterRef.current = null;
      dispatch({ type: "RESET" });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state.callState !== "in_call") return;
    const id = setInterval(() => setWaveTick((t) => t + 1), 200);
    return () => clearInterval(id);
  }, [state.callState]);

  useCallEvents(adapterInstance, callId);

  const handleStart = useCallback(() => {
    if (adapterRef.current) return;
    const toE164 = (raw: string, code: string) => { const d = raw.replace(/\D/g, "").replace(/^0+/, ""); return d ? `${code}${d}` : ""; };
    if (channel === "phone") {
      const err = !phoneNum ? "Enter your number" : validatePhone(phoneNum);
      if (err) { setSetupError(err); return; }
    }
    if (showTransfer && transferNum) {
      const err = validatePhone(transferNum);
      if (err) { setSetupError(`Warm transfer: ${err}`); return; }
    }
    const email = emailBox && showEmail ? testerEmail.trim().toLowerCase() : "";
    if (email) {
      const err = validateTesterEmail(email);
      if (err) { setSetupError(`Your email: ${err}`); return; }
    }
    setSetupError(null);
    startAdapter(new NuplayCallAdapter({
      queryType: scenario,
      channel,
      agentId: agent.agentId ?? agentIdForQuery(scenario),
      workspaceId: agent.workspaceId,
      apiBase: agent.apiBase,
      wsBase: agent.wsBase,
      agentNameOverrides: agent.agentNameOverrides,
      phoneNumber: channel === "phone" ? toE164(phoneNum, dialCode) : undefined,
      transferPhone: showTransfer && transferNum ? toE164(transferNum, transferDialCode) : undefined,
      testerEmail: emailBox ? email : undefined,
    }));
  }, [channel, dialCode, phoneNum, transferDialCode, transferNum, showTransfer, emailBox, showEmail, testerEmail, scenario, agent, startAdapter]);

  const handleEnd = useCallback(() => { adapterRef.current?.disconnect(); dispatch({ type: "CALL_STATE_CHANGE", state: "ended" }); }, [dispatch]);
  const handleMute = useCallback(() => { const next = !state.isMuted; dispatch({ type: "TOGGLE_MUTE" }); (adapterRef.current as NuplayCallAdapter)?.setMuted?.(next); }, [dispatch, state.isMuted]);
  const handleClose = () => { adapterRef.current?.disconnect(); navigate("/nurix-experience/experience"); };
  const handleNewCall = () => { adapterRef.current?.disconnect(); adapterRef.current = null; dispatch({ type: "RESET" }); navigate(`/nurix-experience/experience?agent=${agent.slug}&persona=${persona.id}&query=${scenario}&channel=web`); };
  // Riley / Mike toggle (TSC): the same customer on the other crew, as a fresh session. Off during a call.
  const handleSwitchAgent = (d: TscDemo) => {
    adapterRef.current?.disconnect(); adapterRef.current = null;
    dispatch({ type: "INIT_SESSION", agentSlug: d.agentSlug, sessionId: crypto.randomUUID() });
    dispatch({ type: "SET_PERSONA", personaId: persona.id });
    const next = new URLSearchParams(location.search);
    next.set("agent", d.agentSlug);
    navigate(`${location.pathname}?${next.toString()}`, { replace: true });
  };
  const dismissTour = () => { setTourStep(-1); try { localStorage.setItem("nx-exp-tour", "1"); } catch { /* ignore */ } };

  const connecting = state.callState === "setting_up" || state.callState === "connecting";
  const isPre = state.callState === "idle" || connecting;
  const isActive = state.callState === "in_call";
  const isEnded = state.callState === "ended" || state.callState === "failed";
  const callStarted = state.callState !== "idle"; // live telemetry cards appear only once the call begins
  const callerTurns = state.transcript.filter((t) => t.speaker === "user").length;

  // ── Live order history (Shopify-picked personas have no seeded orders) ──
  const shopCustomerId = shopifyCustomerIdFromPersonaId(persona.id);
  const { data: liveOrders } = useQuery({
    queryKey: ["persona_orders", shopCustomerId],
    queryFn:  () => fetchCustomerOrders({ customerId: shopCustomerId! }, 4),
    enabled:  shopifyConfigured && shopCustomerId != null && persona.orders.length === 0,
    retry:    0,
    staleTime: 60_000,
  });

  // ── Customer data (USD) ──
  const tier = persona.loyaltyTier;
  // Verticals with a playbook reskin a few panels: the tier decides what the agent may
  // PROMISE, so the tester needs that table on screen and the caller's script to hand.
  // Everything else (shell, customer card, activity timeline) is shared with retail.
  const vp = verticalPlaybook(agent.slug);
  const play = playbookFor(agent.slug, persona.id);
  // Tractor Supply runs in its own skin, with suggested lines that follow the call.
  const tscCase = isTscSlug(agent.slug) && persona.id.startsWith(TSC_PERSONA_ID_PREFIX)
    ? getTscCustomer(persona.id.slice(TSC_PERSONA_ID_PREFIX.length))
    : undefined;
  const loc = [persona.city, persona.state].filter(Boolean).join(", ");
  const ltv = persona.lifetimeValueBand ? (LTV_LABEL[persona.lifetimeValueBand] ?? persona.lifetimeValueBand) : "";
  const orders =
    persona.orders.length > 0
      ? persona.orders.map((o) => ({
          id: o.orderId, product: o.product,
          price: o.totalUsd != null ? usd(o.totalUsd) : "",
          statusText: o.statusLabel ?? ORDER_STATUS_TEXT[o.status] ?? humanizeFulfillment(o.status),
          tone: statusTone(o.status), date: o.date, highlight: o.highlight as string | undefined,
        }))
      : (liveOrders ?? []).map((o) => ({
          id: o.name, product: o.lineItems[0]?.title ?? "Order",
          price: o.totalPrice ? `${o.currency} ${parseFloat(o.totalPrice).toFixed(2)}` : "",
          statusText: humanizeFulfillment(o.fulfillmentStatus), tone: fulfillTone(o.fulfillmentStatus), date: "", highlight: undefined as string | undefined,
        }));
  const orderCount = persona.ordersCount ?? orders.length;
  // Spend and LTV are storefront facts. A payments account, a loan book or an apparel
  // loyalty file has neither, and printing "Spent —  LTV —" in the header of a demo is
  // three dead cells where the two numbers that matter should be. Decide from the data
  // rather than a slug list, so every non-retail vertical gets this for free.
  const flaggedCount = persona.orders.filter((o) => o.status === "exception" || o.status === "delayed").length;
  const isAccountStyle = persona.totalSpentUsd == null;
  const verticalStats = getVertical(agent.slug).consoleStats?.(persona);
  const stats = verticalStats
    ? verticalStats
    : isAccountStyle
    ? [
        { k: getVertical(agent.slug).tierLabel ?? "Plan", v: tier || "—" },
        { k: "Open", v: String(orderCount) },
        { k: "Flagged", v: String(flaggedCount) },
      ]
    : [
        { k: "Spent", v: usd(persona.totalSpentUsd as number) },
        { k: "LTV", v: ltv || "—" },
        { k: "Orders", v: String(orderCount) },
      ];
  const contact: { Icon: () => JSX.Element; value: string }[] = [
    { Icon: IcMail, value: persona.email },
    { Icon: IcPhoneSm, value: persona.phone },
    { Icon: IcPin, value: [loc, persona.preferredChannel ? persona.preferredChannel.toUpperCase() : ""].filter(Boolean).join(" · ") },
  ].filter((c) => c.value);

  const firstOrder = orders[0]?.id ?? "your order";
  // Openers come from the caller's own playbook — the opening line plus the pushes that
  // make the tier visible. The generic WISMO openers say nothing on a payments or loans
  // call, and a stranger reading "Where is my order?" to a bank is the bug this fixes.
  const openers = play
    ? [play.opener, ...play.followUps]
    : (OPENERS[scenario] ?? OPENERS.wismo ?? []).map((o) => o.replace("{ORDER}", firstOrder));
  const callHints = play?.hints
    ? play.hints
    : vp
    ? ([
        { k: "Read back this email", v: persona.email },
        { k: "Your name", v: persona.name },
        { k: "Account", v: persona.accountId },
      ].filter((h) => h.v) as { k: string; v: string }[])
    : ([
        { k: "Phone on file", v: persona.phone },
        { k: "Account", v: persona.accountId },
        orders[0] ? { k: "Order ID", v: orders[0].id } : null,
      ].filter(Boolean) as { k: string; v: string }[]);

  // ── Agent Activity events ──
  type Ev = { id: string; kind: "verified" | "tool" | "handoff"; title: string; desc: string; ok: boolean; source?: string; payload?: { k: string; v: string }[]; output?: { k: string; v: string }[]; tool?: { fn: string; input: string; ms: number; isError: boolean } };
  const events: Ev[] = [];
  let toolCount = 0;
  let handedOff = false;
  if ((isActive || isEnded) && !tscCase) {
    events.push({ id: "verified", kind: "verified", title: "Customer identified", desc: `${persona.name} · ${persona.accountId}`, ok: true });
  }
  for (const a of state.liveActions) {
    if (a.type === "tool_call") {
      toolCount++;
      const toolInput = shortVal(Object.values(a.args ?? {})[0] ?? a.args);
      const payload = formatPayload(a.args);
      const output = formatPayload(a.output);
      const source = /shopify/i.test(`${a.functionName} ${JSON.stringify(a.args ?? {})} ${JSON.stringify(a.output ?? {})}`) ? "Shopify" : "";
      events.push({
        id: a.id, kind: "tool", ok: !a.isError,
        title: humanizeFn(a.functionName),
        desc: a.isError ? "Lookup error" : (payload.length || output.length ? "" : "Completed"),
        source,
        payload,
        output,
        tool: { fn: a.functionName, input: toolInput, ms: a.durationMs, isError: a.isError },
      });
    } else if (a.type === "agent_handoff") {
      handedOff = true;
      events.push({ id: a.id, kind: "handoff", ok: true, title: `Routed to ${a.toAgent}`, desc: a.routingCondition || "Agent handoff" });
    }
  }
  const activityHasWork = toolCount > 0 || handedOff;

  // The one discipline check worth automating: the tier is named EXACTLY once, at the
  // moment it changes the outcome. Naming it twice (or opening with it) is the
  // regression this demo hits most, and it is the one that is cheap to spot in text.
  const tierMentions = vp && tier
    ? (state.transcript
        .filter((t) => t.speaker !== "user" && t.speaker !== "tool" && t.speaker !== "system")
        .map((t) => t.text).join("  ")
        .match(new RegExp(`\\b${tier}\\b`, "gi")) || []).length
    : 0;
  const tierCheck = tierMentions === 0 ? "pending" : tierMentions === 1 ? "ok" : "bad";

  // ── Live Workflow stepper: Identify → specialist → Human Handoff ──
  const spec = SPECIALISTS[detectSpecialist(state.transcript, state.liveActions, scenario)];
  const flow = [
    { key: "identify",   label: "Identify" },
    { key: "specialist", label: spec.label },
    { key: "handoff",    label: "Human Handoff" },
  ];
  let activeFlowIdx = -1;
  if (callStarted) activeFlowIdx = 0;                                  // Identify
  if (isActive && (toolCount >= 1 || callerTurns >= 1)) activeFlowIdx = 1; // specialist engaged
  if (handedOff || isEnded) activeFlowIdx = 2;                         // human handoff

  const tscRoute = state.agentPath.map((n) => n.agent);
  const tscTransferred = state.liveActions.some((a) => a.type === "tool_call" && /transfer/i.test(a.functionName));

  const lastIsUser = state.transcript[state.transcript.length - 1]?.speaker === "user";
  const callerFirst = (persona.name || "Caller").trim().split(/\s+/)[0];
  const callerInitial = (persona.name || "?").trim().charAt(0).toUpperCase();
  const orderShort = firstOrder.replace(/\D/g, "").slice(-4) || firstOrder; // last 4 digits for the say-next hint
  const flowHints = (CALL_FLOW_HINTS[scenario] ?? CALL_FLOW_HINTS.wismo ?? []).map((h) => h.replace("{ORDER}", orderShort));
  const lastAgentText = [...state.transcript].reverse().find((t) => t.speaker !== "user" && t.speaker !== "tool" && t.speaker !== "system")?.text ?? "";
  const cueHint = lastAgentText ? AGENT_CUE_HINTS.find((c) => c.re.test(lastAgentText)) : undefined;
  const baseHint = flowHints.length ? flowHints[Math.min(callerTurns, flowHints.length - 1)] : null;
  const liveHint = tscCase
    ? tscLiveHint(tscCase, lastAgentText, callerTurns)
    : cueHint
    ? cueHint.hint.replace("{ORDER}", orderShort).replace("{NAME}", persona.name || "me").replace("{OPENER}", flowHints[0] ?? "")
    : baseHint;

  return (
    <div className={`nx-exp${sideCollapsed ? " side-collapsed" : ""}${tscCase ? " tsc" : ""}${callStarted ? " call-started" : ""}`}>
      <style>{EXP_CSS}</style>
      {tscCase && <style>{TSC_CONSOLE_CSS}</style>}

      {/* ── Left: scenarios + capabilities ── */}
      <aside className="ex-side">
        <div className="ex-side-top">
          {tscCase
            ? <img className="ex-logo tsc-logo" src={TSC_LOGO_SRC} alt="Tractor Supply Co." />
            : <img className="ex-logo" src={sideCollapsed ? "/nuplay-symbol.svg" : "/nuplay-logo-wide.svg"} alt="NuPlay" />}
          <button
            type="button"
            className="ex-collapse"
            title={sideCollapsed ? "Expand panel" : "Collapse panel"}
            aria-label={sideCollapsed ? "Expand panel" : "Collapse panel"}
            onClick={() => setSideCollapsed((v) => !v)}
          ><Ic d="M5 4h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z|M9 4v16" s={17} /></button>
        </div>

        <div className="ex-ws">
          <span className="ex-ws-ic"><Ic d="M3 9l1.4-5h15.2L21 9|M5 9v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9|M9 20v-6h6v6" s={14} /></span>
          <span className="ex-ws-name">{agent.brandName ?? "NuPlay Electronics"}</span>
          <Ic d="M6 9l6 6 6-6" s={15} />
        </div>

        <nav className="ex-nav">
          {NAV.map((g) => (
            <div className="ex-nav-group" key={g.group}>
              <div className="ex-nav-label">{g.group}</div>
              {g.items.map((it) => (
                <a
                  className={`ex-nav-item ${it.active ? "active" : ""}`}
                  key={it.label}
                  href={`${ZERO_BASE}${it.deep ? it.deep(consoleCtx) : it.route}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={it.label}
                >
                  <Ic d={it.d} s={18} /><span>{it.label}</span>
                </a>
              ))}
            </div>
          ))}
        </nav>

        <div className="ex-side-foot">
          <div className="ex-foot-av ex-brand-av"><img src="/nuplay-symbol.svg" alt="NuPlay" /></div>
          <div className="ex-foot-main"><span className="ex-foot-t">NuPlay</span></div>
        </div>
      </aside>

      {/* ── Center: call stage ── */}
      <main className="ex-main">
        <header className="ex-head">
          <div>
            <div className="ex-head-titlerow">
              <h1>{agent.name}</h1>
              <span className={`ex-status ${isActive ? "live" : isEnded ? "ended" : "ready"}`}>
                <span className="ex-dot" />{isActive ? "Live" : isEnded ? "Call ended" : connecting ? "Connecting" : "Ready"}
              </span>
            </div>
            <div className="ex-head-sub">
              <span>{agent.brandName ?? "NuPlay Electronics"} · {agent.brandTagline ?? "Customer Support"}</span>
              {tscCase && <span className="tsc-case-pill">Customer {tscCase.num}</span>}
              {(isActive || isEnded) && <span className="ex-timer">{timerDisplay}</span>}
            </div>
          </div>
          <div className="tsc-head-actions">
            {tscCase && tscDemo && <TscAgentToggle demo={tscDemo} onPick={handleSwitchAgent} disabled={connecting || isActive} disabledNote="Available when the call ends" />}
            {tscCase && <TscResetDemo variant="console" disabled={connecting || isActive} disabledNote="Available when the call ends" />}
            <button className="ex-help" onClick={() => setTourStep(0)} aria-label="Show tips"><IcHelp /></button>
          </div>
        </header>

        <div className="ex-stage">
          {isPre ? (
            <div className="ex-ready">
              <div className="ex-orb"><AgentAvatar src={agent.avatarUrl} initial={tscCase ? agent.personaName.charAt(0) : undefined} /></div>
              <h2 className="ex-ready-h">{connecting ? (channel === "phone" ? "Calling your phone…" : "Connecting…") : "Ready when you are"}</h2>
              <p className="ex-ready-p">{connecting && channel === "phone" ? "Pick up when your phone rings." : `Start a web call, or get the agent to call your phone. Add a number to test a warm transfer to a person.${emailBox ? " Add your email to get the password reset, payment link and order emails yourself." : ""}`}</p>

              <div className="ex-callsetup">
                {agent.supportedChannels.includes("phone") && (
                  <div className="ex-chan">
                    <button type="button" className={`ex-chan-btn ${channel === "web" ? "active" : ""}`} onClick={() => { setChannel("web"); setSetupError(null); }} disabled={connecting}>Web call</button>
                    <button type="button" className={`ex-chan-btn ${channel === "phone" ? "active" : ""}`} onClick={() => { setChannel("phone"); setSetupError(null); }} disabled={connecting}>Phone call</button>
                  </div>
                )}
                {channel === "phone" && (
                  <div className="ex-phone-row">
                    <select className="ex-cc" value={dialCode} onChange={(e) => setDialCode(e.target.value)} disabled={connecting}>
                      <option value="+91">🇮🇳 +91</option>
                      <option value="+1">🇺🇸 +1</option>
                    </select>
                    <input className="ex-field ex-phone-field" type="tel" inputMode="tel" placeholder="Mobile number" value={phoneNum} onChange={(e) => setPhoneNum(e.target.value)} disabled={connecting} />
                  </div>
                )}
                {showTransfer ? (
                  <div className="ex-phone-row">
                    <select className="ex-cc" value={transferDialCode} onChange={(e) => setTransferDialCode(e.target.value)} disabled={connecting}>
                      <option value="+91">🇮🇳 +91</option>
                      <option value="+1">🇺🇸 +1</option>
                    </select>
                    <input className="ex-field ex-phone-field" type="tel" inputMode="tel" placeholder="Warm transfer number" value={transferNum} onChange={(e) => setTransferNum(e.target.value)} disabled={connecting} />
                  </div>
                ) : (
                  <button type="button" className="ex-addxfer" onClick={() => setShowTransfer(true)} disabled={connecting}>+ Add warm transfer</button>
                )}
                {emailBox && (showEmail ? (
                  <input className="ex-field" type="email" inputMode="email" autoComplete="email" spellCheck={false} placeholder={`Your email, for the emails ${agent.personaName} sends`} value={testerEmail} onChange={(e) => setTesterEmail(e.target.value)} disabled={connecting} />
                ) : (
                  <button type="button" className="ex-addxfer" onClick={() => setShowEmail(true)} disabled={connecting}>+ Add your email</button>
                ))}
                {setupError && <div className="ex-setup-err">{setupError}</div>}
                <button className="ex-start" onClick={handleStart} disabled={connecting}>
                  {connecting ? (channel === "phone" ? "Calling…" : "Connecting…") : channel === "phone" ? "Call my phone" : "Start call"}
                </button>
              </div>

              <div className="ex-tryasking">
                <div className="ex-section-label"><IcChat /> Try asking</div>
                <div className="ex-openers">
                  {openers.map((o) => (
                    <button key={o} className="ex-opener" onClick={handleStart}><span className="ex-quote">“</span>{o}</button>
                  ))}
                </div>
              </div>

              {callHints.length > 0 && (
                <div className="ex-hints">
                  <div className="ex-section-label">Call hints</div>
                  <div className="ex-hints-list">
                    {callHints.map((h) => (
                      <div className="ex-hint" key={h.k}><span className="ex-hint-k">{h.k}</span><span className="ex-hint-v">{h.v}</span></div>
                    ))}
                  </div>
                </div>
              )}

              <div className="ex-caps">
                <div className="ex-section-label">What this agent can do</div>
                <div className="ex-caps-list">
                  {(vp?.capabilities ?? CAPABILITIES).map((c) => (
                    <div className="ex-cap" key={c.t}>
                      <span className="ex-cap-ic"><IcCheckmark s={12} /></span>
                      <span className="ex-cap-main"><span className="ex-cap-t">{c.t}</span><span className="ex-cap-d">{c.d}</span></span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="ex-convo">
                {state.transcript.map((t) => {
                  if (t.speaker === "user") {
                    return (
                      <div key={t.id} className="ex-row user">
                        <div className="ex-caller-wrap">
                          <div><div className="ex-spk">{callerFirst}</div><div className="ex-bubble ex-bubble-user">{t.text}</div></div>
                          <div className="ex-caller-av"><PersonaAvatar src={persona.photoUrl} initials={callerInitial} /></div>
                        </div>
                      </div>
                    );
                  }
                  if (t.speaker === "tool" || t.speaker === "system") return null;
                  return (
                    <div key={t.id} className="ex-row">
                      <div className="ex-agent-wrap">
                        <div className="ex-voice-dot"><AgentAvatar src={agent.avatarUrl} initial={tscCase ? agent.personaName.charAt(0) : undefined} /></div>
                        <div><div className="ex-spk">{agent.personaName ?? "Agent"}</div><div className="ex-bubble ex-bubble-agent">{t.text}</div></div>
                      </div>
                    </div>
                  );
                })}
                {isActive && state.transcript.length === 0 && (
                  <div className="ex-convo-loading">
                    <div className="ex-loader-mark"><img src="/nuplay-symbol.svg" alt="NuPlay" /></div>
                    <div className="ex-loader-text">Listening for the conversation…</div>
                    <div className="ex-loader-sub">Connecting you with {agent.personaName ?? agent.name}. The transcript appears here live.</div>
                  </div>
                )}
                {isActive && state.transcript.length > 0 && lastIsUser && (
                  <div className="ex-row">
                    <div className="ex-agent-wrap">
                      <div className="ex-voice-dot"><AgentAvatar src={agent.avatarUrl} initial={tscCase ? agent.personaName.charAt(0) : undefined} /></div>
                      <div className="ex-thinking"><span></span><span></span><span></span></div>
                    </div>
                  </div>
                )}
                {isEnded && state.transcript.length === 0 && <div className="ex-ended-note">Call ended. No transcript was captured.</div>}
              </div>

              {isEnded ? (
                <div className="ex-endbar"><button className="ex-restart" onClick={handleNewCall}>Try another scenario</button></div>
              ) : (
                <div className="ex-mic">
                  {isActive && liveHint && (
                    <div className="ex-live-hint"><span className="ex-live-hint-k">Say next</span><span className="ex-live-hint-v">“{liveHint}”</span></div>
                  )}
                  {isActive && <div className="ex-listening"><span className="ex-listening-dot" />Listening…</div>}
                  <div key={waveTick} className="ex-wave">
                    {Array.from({ length: 28 }, (_, i) => (
                      <span key={i} style={{ height: `${isActive ? 3 + Math.abs(Math.sin(i * 0.7 + waveTick * 0.4)) * 16 : 3}px` }} />
                    ))}
                  </div>
                  <div className="ex-mic-row">
                    <button className="ex-endbtn" onClick={handleEnd} aria-label="End call"><IcX /></button>
                    <button className={`ex-micbtn ${state.isMuted ? "muted" : ""}`} onClick={handleMute} aria-label={state.isMuted ? "Unmute" : "Mute"}><IcMic /></button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* ── Right: customer context ── */}
      <aside className="ex-panel">
        <div className="ex-cust">
          <div className="ex-cust-head">
            <div className="ex-cust-av"><PersonaAvatar src={persona.photoUrl} initials={persona.avatarInitials} /></div>
            <div className="ex-cust-id">
              <div className="ex-cust-name">{persona.name}{tier && <span className="ex-tier">{tier}</span>}</div>
              <div className="ex-cust-acct">{persona.accountId}</div>
            </div>
          </div>
          <div className="ex-stats">
            {stats.map((s) => (
              <div className="ex-stat" key={s.k}><span className="ex-stat-k">{s.k}</span><span className="ex-stat-v">{s.v}</span></div>
            ))}
          </div>
          <div className="ex-contact">
            {contact.map((c, i) => (
              <div className="ex-contact-row" key={i}><span className="ex-contact-ic"><c.Icon /></span><span className="ex-contact-v" title={c.value}>{c.value}</span></div>
            ))}
          </div>
          {persona.crmNotes && (
            <div className="ex-intent"><span className="ex-intent-k">{tscCase ? "What this tests" : "Intent"}</span><span className="ex-intent-v">{persona.crmNotes}</span></div>
          )}
        </div>

        {orders.length > 0 && (
          <div className="ex-orders">
            <div className="ex-orders-head"><IcBox /> Orders <span className="ex-orders-n">({orders.length})</span></div>
            <div className="ex-orders-list">
              {orders.map((o) => (
                <div className="ex-order" key={o.id}>
                  <span className="ex-order-ic"><IcBox /></span>
                  <div className="ex-order-main">
                    <div className="ex-order-r1">
                      <span className="ex-order-prod" title={o.product}>{o.product}</span>
                      <span className={`ex-order-st tone-${o.tone}`}>{o.statusText}</span>
                    </div>
                    <div className="ex-order-r2">
                      {o.id}{o.price ? ` · ${o.price}` : ""}{o.date ? ` · Ordered ${o.date}` : ""}
                      {o.highlight ? <span className="ex-order-hl">{o.highlight}</span> : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {vp && tier && vp.policy.length > 0 && (
          <div className="ex-card">
            <div className="ex-card-head">
              What {tier} may promise
              <span className={`ex-tiercheck ${tierCheck}`}>
                {tierCheck === "pending" ? "plan unnamed" : tierCheck === "ok" ? "named once ✓" : `named ${tierMentions}× ✗`}
              </span>
            </div>
            <p className="ex-cardnote">{vp.agentName} reads every promise out of this table. Anything offered that is not here was invented.</p>
            <div className="ex-policy">
              {vp.policy.map((row) => (
                <div className="ex-policy-row" key={row.label}>
                  <span className="ex-policy-k">{row.label}</span>
                  <span className="ex-policy-v">{vp.tierValue(row, tier)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {vp && play && play.facts.length > 0 && (
          <div className="ex-card ex-facts">
            <div className="ex-card-head">Real figures on this account</div>
            <p className="ex-cardnote">Straight from the seeded database. If {vp.agentName}'s number differs, that is a genuine finding.</p>
            <div className="ex-policy">
              {play.facts.map((f) => (
                <div className="ex-policy-row" key={f.label + f.value}>
                  <span className="ex-policy-k">{f.label}</span>
                  <span className="ex-policy-v ex-policy-num">{f.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {callStarted && (<>
        <div className="ex-card ex-wf">
          <div className="ex-card-head">{tscCase ? "Crew routing" : "Live Workflow"}<span className={`ex-rt ${isActive ? "on" : ""}`}><span className="ex-dot" />{isActive ? "Real-time" : isEnded ? "Ended" : "Connecting"}</span></div>
          {tscCase ? (
            <div className="tsc-route">
              {tscRoute.length === 0 && <div className="ex-empty">{`${agent.personaName}'s agents appear here as the call moves between them.`}</div>}
              {tscRoute.map((name, i) => {
                const active = i === tscRoute.length - 1 && !tscTransferred && !isEnded;
                return (
                  <div className={`tsc-route-node ${active ? "active" : "done"}`} key={`${name}-${i}`}>
                    <span className="tsc-route-dot">{active ? <span className="ex-step-ring" /> : <IcCheckmark s={11} />}</span>
                    <span className="tsc-route-name">{name}</span>
                    {i === 0 && <span className="tsc-route-tag">start</span>}
                  </div>
                );
              })}
              {tscTransferred && (
                <div className={`tsc-route-node ${isEnded ? "done" : "active"} human`}>
                  <span className="tsc-route-dot">{isEnded ? <IcCheckmark s={11} /> : <span className="ex-step-ring" />}</span>
                  <span className="tsc-route-name">Teammate</span>
                  <span className="tsc-route-tag">warm transfer</span>
                </div>
              )}
            </div>
          ) : (
          <div className="ex-stepper">
            {flow.filter((_, i) => i <= activeFlowIdx).map((f, i) => {
              const st = i < activeFlowIdx ? "done" : "active";
              return (
                <div className={`ex-step ${st}`} key={f.key}>
                  <span className="ex-step-node">{st === "done" ? <IcCheckmark s={12} /> : <span className="ex-step-ring" />}</span>
                  <span className="ex-step-label">{f.label}</span>
                </div>
              );
            })}
          </div>
          )}
        </div>

        <div className="ex-card ex-act">
          <div className="ex-card-head"><IcActivity /> Agent Activity</div>
          {!activityHasWork ? (
            <div className="ex-empty">Tool calls will appear here once the call starts</div>
          ) : (
            <div className="ex-timeline">
              {events.map((e) => {
                const hasBody = (e.payload && e.payload.length > 0) || (e.output && e.output.length > 0);
                if (!hasBody) {
                  return (
                    <div className="ex-event" key={e.id}>
                      <span className={`ex-node ${e.ok ? "ok" : "err"}`} />
                      <div className="ex-event-main"><h4>{e.title}{e.source ? <span className="ex-src">{e.source}</span> : null}</h4><p>{e.desc}</p></div>
                      {e.kind === "verified" ? <span className="ex-vpill">verified</span> : <span className="ex-ms">{e.tool ? `${e.tool.ms}ms` : ""}</span>}
                    </div>
                  );
                }
                return (
                  <details className="ex-event-d" key={e.id}>
                    <summary className="ex-event-sum">
                      <span className={`ex-node ${e.ok ? "ok" : "err"}`} />
                      <div className="ex-event-main"><h4>{e.title}{e.source ? <span className="ex-src">{e.source}</span> : null}</h4></div>
                      {e.tool ? <span className="ex-ms">{e.tool.ms}ms</span> : null}
                      <span className="ex-caret"><Ic d="M9 6l6 6-6 6" s={13} /></span>
                    </summary>
                    <div className="ex-event-body">
                      {e.payload && e.payload.length > 0 && (
                        <ul className="ex-bullets">
                          {e.payload.map((p, i) => <li key={i}><span className="ex-bk">{p.k}</span>{p.v}</li>)}
                        </ul>
                      )}
                      {e.output && e.output.length > 0 && (
                        <>
                          <div className="ex-bullets-h">Result</div>
                          <ul className="ex-bullets ex-bullets-out">
                            {e.output.map((p, i) => <li key={i}><span className="ex-bk">{p.k}</span>{p.v}</li>)}
                          </ul>
                        </>
                      )}
                    </div>
                  </details>
                );
              })}
            </div>
          )}
        </div>

        </>)}

        {/* Post-call analysis. Mounted only once the call has ENDED — the platform scores
            the transcript asynchronously afterwards, so there is nothing to show before. */}
        {isEnded && agent.agentId && agent.workspaceId && (
          <PostCallAnalytics
            agentId={agent.agentId}
            workspaceId={agent.workspaceId}
            callEndedAt={state.endedAt ?? Date.now()}
          />
        )}
      </aside>

      {/* ── Onboarding tour ── */}
      {tourStep >= 0 && tourStep < TOUR.length && (
        <div className="ex-tour-backdrop" onClick={dismissTour}>
          <div className="ex-tour" onClick={(e) => e.stopPropagation()}>
            <div className="ex-tour-step">Tip {tourStep + 1} of {TOUR.length}</div>
            <h3>{TOUR[tourStep].k}</h3>
            <p>{TOUR[tourStep].b}</p>
            <div className="ex-tour-foot">
              <div className="ex-tour-dots">{TOUR.map((_, i) => <span key={i} className={i === tourStep ? "on" : ""} />)}</div>
              <div className="ex-tour-btns">
                <button className="ex-tour-skip" onClick={dismissTour}>Skip</button>
                <button className="ex-tour-next" onClick={() => (tourStep < TOUR.length - 1 ? setTourStep(tourStep + 1) : dismissTour())}>
                  {tourStep < TOUR.length - 1 ? "Next" : "Got it"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <button className="ex-close" onClick={handleClose} aria-label="Exit"><IcX s={15} /></button>
    </div>
  );
}

const EXP_CSS = `
.nx-exp { --acc:#2582EB; --acc2:#1D6BD8; --soft:#EFF8FF; --ink:#171717; --muted:#737373; --faint:#A3A3A3; --line:#E5E5E5; --card:#ffffff; --bg:#FAFAFA; --side:#0A0A0A; --side2:#171717; position:fixed; inset:0; display:grid; grid-template-columns:240px minmax(280px,1fr) 360px; background:var(--bg); color:var(--ink); font-family:Nunito,system-ui,sans-serif; overflow:hidden; z-index:50; -webkit-font-smoothing:antialiased; }
@media (max-width:1080px){ .nx-exp { grid-template-columns:208px minmax(240px,1fr) 312px; } }
.nx-exp * { box-sizing:border-box; }
.nx-exp h1, .nx-exp h2, .nx-exp h3, .nx-exp h4 { font-family:"Nunito",system-ui,-apple-system,sans-serif; }

/* Left sidebar */
.ex-side { background:var(--side); color:#e8ecf6; padding:22px 18px; display:flex; flex-direction:column; overflow:hidden; }
.ex-brand { display:flex; align-items:center; margin-bottom:26px; padding:2px 2px; }
.ex-logo { height:24px; width:auto; filter:brightness(0) invert(1); }
.ex-side-label { font-size:10px; font-weight:700; letter-spacing:.12em; text-transform:uppercase; color:#6b7794; margin:6px 4px 11px; }
.ex-scn-list { display:flex; flex-direction:column; gap:4px; margin-bottom:24px; }
.ex-scn { display:flex; align-items:flex-start; gap:11px; text-align:left; background:none; border:none; border-radius:11px; padding:11px 12px; cursor:pointer; color:#aab3c9; transition:background .15s ease; }
.ex-scn:hover:not(:disabled) { background:rgba(255,255,255,.04); }
.ex-scn.active { background:rgba(255,255,255,.07); color:#fff; }
.ex-scn:disabled { cursor:default; }
.ex-scn-dot { width:8px; height:8px; border-radius:50%; background:#3a4straight; background:#39435f; margin-top:5px; flex-shrink:0; }
.ex-scn-dot.on { background:#34d399; box-shadow:0 0 0 3px rgba(52,211,153,.18); }
.ex-scn-main { display:flex; flex-direction:column; gap:2px; min-width:0; }
.ex-scn-t { font-size:13.5px; font-weight:600; }
.ex-scn-d { font-size:11px; color:#717c97; }
.ex-scn.active .ex-scn-d { color:#9aa4bd; }
.ex-cap-list { display:flex; flex-direction:column; gap:13px; }
.ex-cap { display:flex; align-items:flex-start; gap:10px; }
.ex-cap-ic { width:18px; height:18px; border-radius:50%; background:rgba(52,211,153,.16); color:#34d399; display:grid; place-items:center; flex-shrink:0; margin-top:1px; }
.ex-cap-main { display:flex; flex-direction:column; gap:1px; }
.ex-cap-t { font-size:13px; font-weight:600; color:#e8ecf6; }
.ex-cap-d { font-size:11px; color:#717c97; }
.ex-side-foot { margin-top:8px; padding-top:16px; display:flex; align-items:center; gap:11px; border-top:1px solid rgba(255,255,255,.07); flex-shrink:0; }
.ex-foot-av { width:34px; height:34px; border-radius:50%; background:linear-gradient(135deg,var(--acc),var(--acc2)); color:#fff; font-size:12px; font-weight:700; display:grid; place-items:center; flex-shrink:0; }
.ex-foot-main { display:flex; flex-direction:column; }
.ex-foot-t { font-size:13px; font-weight:600; color:#fff; }
.ex-foot-d { font-size:11px; color:#717c97; }

/* Center */
.ex-main { display:flex; flex-direction:column; min-width:0; padding:24px 30px; overflow:hidden; }
.ex-head { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:18px; flex-shrink:0; }
.ex-head-titlerow { display:flex; align-items:center; gap:12px; }
.ex-head h1 { font-size:22px; font-weight:700; letter-spacing:-0.02em; }
.ex-status { display:inline-flex; align-items:center; gap:6px; font-size:11px; font-weight:600; padding:4px 11px; border-radius:999px; background:#f1f5f9; color:var(--muted); }
.ex-status .ex-dot { width:6px; height:6px; border-radius:50%; background:currentColor; }
.ex-status.ready { background:#eef2ff; color:var(--acc); }
.ex-status.live { background:#f0fdf4; color:#16a34a; }
.ex-status.live .ex-dot { animation:expulse 1.5s infinite; }
.ex-status.ended { background:#f1f5f9; color:var(--muted); }
.ex-head-sub { display:flex; align-items:center; gap:12px; color:var(--faint); font-size:13px; margin-top:7px; }
.ex-timer { font-variant-numeric:tabular-nums; padding:2px 9px; background:#f1f5f9; border-radius:999px; color:var(--muted); font-weight:500; }
.ex-help { width:34px; height:34px; border-radius:50%; border:1px solid var(--line); background:var(--card); color:var(--muted); display:grid; place-items:center; cursor:pointer; }
.ex-help:hover { color:var(--ink); }
@keyframes expulse { 0%,100%{opacity:.4;} 50%{opacity:1;} }

.ex-stage { flex:1; min-height:0; background:var(--card); border:1px solid var(--line); border-radius:22px; box-shadow:0 1px 2px rgba(16,24,40,.03); display:flex; flex-direction:column; overflow:hidden; }

/* Ready state */
.ex-ready { flex:1; overflow-y:auto; display:flex; flex-direction:column; align-items:center; text-align:center; padding:42px 30px; }
.ex-orb { width:84px; height:84px; flex-shrink:0; border-radius:50%; background:linear-gradient(135deg,var(--acc),var(--acc2)); color:#fff; display:grid; place-items:center; box-shadow:0 14px 38px rgba(37,130,235,.34); margin-bottom:22px; overflow:hidden; }
.ex-orb .ex-voice-img { width:84px; height:84px; }
.ex-ready-h { font-size:22px; font-weight:700; letter-spacing:-0.02em; margin-bottom:8px; }
.ex-ready-p { font-size:13.5px; color:var(--muted); max-width:330px; line-height:1.55; margin-bottom:22px; }
.ex-callsetup { display:flex; flex-direction:column; align-items:stretch; gap:10px; width:100%; max-width:300px; }
.ex-chan { display:flex; gap:5px; padding:4px; background:var(--bg); border:1px solid var(--line); border-radius:12px; }
.ex-chan-btn { flex:1; border:none; background:transparent; color:var(--muted); font-size:12.5px; font-weight:600; padding:8px; border-radius:9px; cursor:pointer; }
.ex-chan-btn.active { background:var(--card); color:var(--acc); box-shadow:0 1px 3px rgba(16,24,40,.1); }
.ex-phone-row { display:flex; gap:8px; }
.ex-phone-field { flex:1; width:auto; min-width:0; }
.ex-cc { height:42px; border:1px solid var(--line); border-radius:12px; padding:0 8px; font-size:13px; font-weight:600; color:var(--ink); background:var(--card); cursor:pointer; flex-shrink:0; outline:none; }
.ex-cc:focus { border-color:rgba(37,130,235,.5); }
.ex-cc-static { display:grid; place-items:center; height:42px; min-width:50px; padding:0 12px; border:1px solid var(--line); border-radius:12px; font-size:13px; font-weight:600; color:var(--muted); background:var(--bg); flex-shrink:0; }
.ex-field { width:100%; height:42px; padding:0 14px; border:1px solid var(--line); border-radius:12px; font-size:13.5px; color:var(--ink); outline:none; transition:border-color .15s ease; }
.ex-field:focus { border-color:rgba(37,130,235,.5); }
.ex-field::placeholder { color:var(--faint); }
.ex-addxfer { align-self:center; background:none; border:none; color:var(--acc); font-size:12.5px; font-weight:600; cursor:pointer; padding:2px; }
.ex-setup-err { font-size:12px; color:#dc2626; text-align:center; }
.ex-start { padding:13px 30px; border-radius:14px; border:none; color:#fff; font-size:14.5px; font-weight:600; background:linear-gradient(135deg,var(--acc),var(--acc2)); box-shadow:0 8px 22px rgba(37,130,235,.3); cursor:pointer; transition:transform .12s; }
.ex-start:hover:not(:disabled) { transform:translateY(-1px); }
.ex-start:disabled { opacity:.7; cursor:default; }
.ex-section-label { display:flex; align-items:center; gap:7px; font-size:11px; font-weight:700; letter-spacing:.07em; text-transform:uppercase; color:var(--faint); margin-bottom:12px; }
.ex-section-label svg { color:var(--acc); }
.ex-tryasking { margin-top:34px; width:100%; max-width:340px; }
.ex-openers { display:flex; flex-direction:column; gap:9px; }
.ex-opener { display:flex; align-items:center; gap:4px; text-align:left; border:1px solid var(--line); background:var(--card); color:var(--ink); font-size:13.5px; font-weight:500; padding:12px 16px; border-radius:13px; cursor:pointer; transition:all .14s ease; }
.ex-opener:hover { border-color:var(--acc); color:var(--acc); box-shadow:0 4px 14px rgba(37,130,235,.12); }
.ex-quote { color:var(--faint); font-size:18px; line-height:0; }
.ex-hints { margin-top:30px; width:100%; max-width:340px; }
.ex-hints-list { display:flex; flex-direction:column; gap:1px; border:1px solid var(--line); border-radius:12px; overflow:hidden; }
.ex-hint { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:10px 14px; background:var(--bg); }
.ex-hint-k { font-size:11px; color:var(--faint); font-weight:600; text-transform:uppercase; letter-spacing:.04em; }
.ex-hint-v { font-size:13px; color:var(--ink); font-weight:600; font-variant-numeric:tabular-nums; }

/* Conversation */
.ex-convo { flex:1; overflow-y:auto; padding:26px; }
.ex-row { display:flex; margin-bottom:16px; }
.ex-row.user { justify-content:flex-end; }
.ex-spk { font-size:10px; color:var(--faint); margin-bottom:6px; font-weight:600; letter-spacing:.04em; text-transform:uppercase; }
.ex-row.user .ex-spk { text-align:right; }
.ex-bubble { max-width:360px; padding:12px 16px; border-radius:16px; font-size:14px; line-height:1.55; }
.ex-bubble-agent { background:var(--soft); color:#1e293b; border-bottom-left-radius:5px; }
.ex-bubble-user { background:var(--acc); color:#fff; border-bottom-right-radius:5px; }
.ex-agent-wrap { display:flex; gap:11px; align-items:flex-start; }
.ex-caller-wrap { display:flex; gap:11px; align-items:flex-start; }
.ex-caller-av { width:32px; height:32px; border-radius:50%; background:#F9F9F9; color:#525252; font-size:12px; font-weight:700; display:grid; place-items:center; flex-shrink:0; margin-top:18px; }
.ex-live-hint { display:flex; align-items:center; gap:8px; justify-content:center; margin-bottom:12px; padding:7px 12px; background:#f5f7ff; border:1px solid #e6ebff; border-radius:999px; max-width:100%; }
.ex-live-hint-k { font-size:9px; font-weight:700; letter-spacing:.06em; text-transform:uppercase; color:var(--acc); background:#fff; border:1px solid #e6ebff; padding:2px 7px; border-radius:999px; flex-shrink:0; }
.ex-live-hint-v { font-size:12.5px; color:#475569; font-weight:600; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.ex-voice-dot { width:32px; height:32px; border-radius:50%; background:var(--soft); color:var(--acc); display:grid; place-items:center; flex-shrink:0; margin-top:18px; overflow:hidden; }
.ex-voice-img { width:100%; height:100%; object-fit:cover; border-radius:50%; }
.ex-person-img { width:100%; height:100%; object-fit:cover; border-radius:50%; display:block; }
.ex-cust-av, .ex-caller-av { overflow:hidden; }
.ex-thinking { padding:13px 16px; border-radius:16px; border-bottom-left-radius:5px; background:var(--soft); display:flex; gap:6px; align-self:flex-end; }
.ex-thinking span { width:6px; height:6px; background:var(--acc); border-radius:50%; animation:exbounce 1.2s infinite ease-in-out; }
.ex-thinking span:nth-child(2){animation-delay:.15s;} .ex-thinking span:nth-child(3){animation-delay:.3s;}
@keyframes exbounce { 0%,100%{opacity:.3;transform:translateY(0);} 50%{opacity:1;transform:translateY(-3px);} }
.ex-ended-note { height:100%; display:flex; align-items:center; justify-content:center; color:var(--faint); font-size:13px; }
/* Empty-transcript loader (call live, waiting for the first turn) */
.ex-convo-loading { height:100%; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:15px; text-align:center; padding:20px; }
.ex-loader-mark { position:relative; width:72px; height:72px; display:grid; place-items:center; }
.ex-loader-mark::before { content:""; position:absolute; inset:8px; border-radius:50%; background:radial-gradient(circle, rgba(37,130,235,.18), transparent 68%); animation:exglow 1.8s ease-in-out infinite; }
/* spinning ring — the NuPlay mark itself stays upright */
.ex-loader-mark::after { content:""; position:absolute; inset:0; border-radius:50%; background:conic-gradient(from 0deg, transparent 0deg 120deg, var(--acc) 320deg, var(--acc2) 360deg); -webkit-mask:radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px)); mask:radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px)); animation:exspin 0.9s linear infinite; }
.ex-loader-mark img { position:relative; z-index:1; width:42px; height:42px; }
.ex-loader-text { font-size:14.5px; font-weight:600; color:var(--ink); }
.ex-loader-sub { font-size:12.5px; color:var(--faint); max-width:250px; line-height:1.5; }
@keyframes exspin { to { transform:rotate(360deg); } }
@keyframes exglow { 0%,100%{ transform:scale(.82); opacity:.45; } 50%{ transform:scale(1.08); opacity:.9; } }
.ex-mic { flex-shrink:0; padding:14px 26px 22px; border-top:1px solid var(--line); }
.ex-listening { display:flex; align-items:center; justify-content:center; gap:7px; font-size:12px; color:var(--muted); font-weight:500; margin-bottom:10px; }
.ex-listening-dot { width:7px; height:7px; border-radius:50%; background:#16a34a; animation:expulse 1.4s infinite; }
.ex-wave { display:flex; align-items:center; justify-content:center; gap:3px; height:22px; margin-bottom:14px; }
.ex-wave span { width:3px; border-radius:3px; background:rgba(37,130,235,.45); transition:height .2s ease; }
.ex-mic-row { display:flex; align-items:center; justify-content:center; gap:16px; }
.ex-micbtn { width:54px; height:54px; border-radius:50%; border:none; background:var(--acc); color:#fff; display:grid; place-items:center; box-shadow:0 6px 18px rgba(37,130,235,.3); cursor:pointer; transition:transform .12s; }
.ex-micbtn:active { transform:scale(.95); } .ex-micbtn.muted { background:#e2e8f0; color:var(--muted); box-shadow:none; }
.ex-endbtn { width:44px; height:44px; border-radius:50%; border:1px solid #fde2e2; background:#fef2f2; color:#ef4444; display:grid; place-items:center; cursor:pointer; }
.ex-endbar { flex-shrink:0; padding:18px 26px; border-top:1px solid var(--line); }
.ex-restart { width:100%; padding:13px; border-radius:13px; border:none; color:#fff; font-size:14px; font-weight:600; background:linear-gradient(135deg,var(--acc),var(--acc2)); box-shadow:0 6px 18px rgba(37,130,235,.28); cursor:pointer; }
.ex-restart:hover { filter:brightness(1.05); }

/* Right panel */
/* min-height:0 is load-bearing — a grid item's auto min-height is its CONTENT height,
   so without it overflow-y:auto never engages and the tail of the panel is simply
   clipped by the .nx-exp overflow:hidden with no way to reach it. */
.ex-panel { background:var(--bg); border-left:1px solid var(--line); padding:22px 22px 28px; display:flex; flex-direction:column; gap:16px; overflow-y:auto; min-height:0; }
.ex-cust { background:var(--card); border:1px solid var(--line); border-radius:18px; padding:18px; }
.ex-cust-head { display:flex; align-items:center; gap:13px; margin-bottom:16px; }
.ex-cust-av { width:46px; height:46px; border-radius:50%; background:#F9F9F9; color:#525252; font-size:14px; font-weight:700; display:grid; place-items:center; flex-shrink:0; }
.ex-cust-name { font-size:16px; font-weight:700; display:flex; align-items:center; gap:8px; }
.ex-tier { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#b45309; background:#fef3c7; padding:2px 8px; border-radius:999px; }
.ex-cust-acct { font-size:12px; color:var(--faint); margin-top:3px; font-variant-numeric:tabular-nums; }
.ex-stats { display:grid; grid-template-columns:repeat(3,1fr); gap:9px; margin-bottom:16px; }
.ex-stat { background:var(--bg); border:1px solid var(--line); border-radius:12px; padding:10px 12px; display:flex; flex-direction:column; gap:3px; }
.ex-stat-k { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--faint); }
.ex-stat-v { font-size:15px; font-weight:600; letter-spacing:-0.01em; }
.ex-contact { display:flex; flex-direction:column; gap:10px; }
.ex-contact-row { display:flex; align-items:center; gap:10px; font-size:13.5px; font-weight:400; line-height:1.4; color:var(--ink); min-width:0; }
.ex-contact-ic { color:var(--faint); flex-shrink:0; display:grid; place-items:center; }
.ex-contact-v { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.ex-intent { margin-top:14px; padding:11px 13px; background:#f5f7ff; border:1px solid #e6ebff; border-radius:12px; display:flex; flex-direction:column; gap:3px; }
.ex-intent-k { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--acc); }
.ex-intent-v { font-size:13.5px; font-weight:500; line-height:1.45; color:#334155; }

.ex-orders { background:var(--card); border:1px solid var(--line); border-radius:18px; padding:16px 18px; }
.ex-orders-head { display:flex; align-items:center; gap:8px; font-size:14px; font-weight:700; margin-bottom:12px; }
.ex-orders-head svg { color:var(--faint); }
.ex-orders-n { color:var(--faint); font-weight:600; }
.ex-orders-list { display:flex; flex-direction:column; gap:9px; }
.ex-order { display:flex; gap:11px; align-items:flex-start; border:1px solid var(--line); border-radius:13px; padding:11px 13px; }
.ex-order-ic { width:30px; height:30px; border-radius:9px; background:var(--soft); color:var(--acc); display:grid; place-items:center; flex-shrink:0; }
.ex-order-main { flex:1; min-width:0; }
.ex-order-r1 { display:flex; align-items:center; justify-content:space-between; gap:10px; }
.ex-order-prod { font-size:13.5px; font-weight:600; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.ex-order-st { flex-shrink:0; font-size:10px; font-weight:600; padding:3px 9px; border-radius:999px; white-space:nowrap; }
.ex-order-r2 { font-size:11.5px; color:var(--muted); margin-top:4px; font-variant-numeric:tabular-nums; }
.ex-order-hl { display:inline-block; margin-left:6px; font-size:10px; font-weight:700; padding:1px 7px; border-radius:999px; background:#f0fdf4; color:#16a34a; vertical-align:1px; }
.tone-green { background:#f0fdf4; color:#16a34a; }
.tone-amber { background:#fffbeb; color:#d97706; }
.tone-red { background:#fef2f2; color:#dc2626; }
.tone-grey { background:#f1f5f9; color:#64748b; }

.ex-card { background:var(--card); border:1px solid var(--line); border-radius:18px; padding:16px 18px; }
.ex-card-head { display:flex; align-items:center; gap:8px; font-size:14px; font-weight:700; margin-bottom:15px; }
.ex-card-head svg { color:var(--faint); }
.ex-rt { margin-left:auto; display:inline-flex; align-items:center; gap:5px; font-size:10px; font-weight:600; padding:3px 9px; border-radius:999px; background:#f1f5f9; color:var(--muted); }

/* Fintech: the tier-entitlement table and the account's real figures. Same two-column
   key/value rhythm as .ex-stat so it reads as part of the customer context, not a
   bolted-on panel. */
.ex-cardnote { font-size:11.5px; line-height:1.5; color:var(--muted); margin:-9px 0 12px; }
.ex-policy { display:flex; flex-direction:column; }
.ex-policy-row { display:grid; grid-template-columns:104px 1fr; gap:12px; align-items:baseline; padding:7px 0; border-bottom:1px solid var(--line); }
.ex-policy-row:last-child { border-bottom:none; padding-bottom:0; }
.ex-policy-k { font-size:11px; font-weight:700; color:var(--faint); line-height:1.4; }
.ex-policy-v { font-size:12.5px; font-weight:600; line-height:1.45; color:var(--ink); }
.ex-policy-num { font-variant-numeric:tabular-nums; }
.ex-tiercheck { margin-left:auto; flex-shrink:0; font-size:10px; font-weight:700; padding:3px 9px; border-radius:999px; background:#f1f5f9; color:var(--muted); white-space:nowrap; }
.ex-tiercheck.ok { background:#f0fdf4; color:#16a34a; }
.ex-tiercheck.bad { background:#fef2f2; color:#dc2626; }
.ex-rt .ex-dot { width:6px; height:6px; border-radius:50%; background:currentColor; }
.ex-rt.on { background:#f0fdf4; color:#16a34a; } .ex-rt.on .ex-dot { animation:expulse 1.5s infinite; }

/* Workflow stepper (horizontal) */
.ex-stepper { display:flex; align-items:flex-start; justify-content:space-between; position:relative; }
.ex-step:not(:first-child)::before { content:""; position:absolute; top:13px; right:50%; left:-50%; height:2px; background:#22c55e; z-index:0; }
.ex-step { position:relative; display:flex; flex-direction:column; align-items:center; gap:8px; flex:1; z-index:1; }
.ex-step-node { width:28px; height:28px; border-radius:50%; display:grid; place-items:center; background:var(--card); border:2px solid var(--line); color:#fff; }
.ex-step-ring { width:8px; height:8px; border-radius:50%; background:var(--line); }
.ex-step-label { font-size:11px; font-weight:600; color:var(--faint); text-align:center; }
.ex-step.done .ex-step-node { background:#22c55e; border-color:#22c55e; color:#fff; }
.ex-step.done .ex-step-label { color:var(--muted); }
.ex-step.active .ex-step-node { border-color:var(--acc); box-shadow:0 0 0 4px rgba(37,130,235,.14); }
.ex-step.active .ex-step-ring { background:var(--acc); }
.ex-step.active .ex-step-label { color:var(--acc); }

.ex-act { flex:1; }
.ex-empty { color:var(--faint); font-size:12.5px; line-height:1.5; padding:14px 0; }
.ex-timeline { position:relative; padding-left:24px; }
.ex-timeline::before { content:""; position:absolute; left:5px; top:6px; bottom:8px; width:1.5px; background:var(--line); }
.ex-event { position:relative; display:flex; align-items:flex-start; gap:8px; padding:9px 0; }
.ex-node { position:absolute; left:-24px; top:11px; width:11px; height:11px; border-radius:50%; background:#22c55e; border:2px solid var(--card); box-shadow:0 0 0 1.5px #22c55e; }
.ex-node.err { background:#ef4444; box-shadow:0 0 0 1.5px #ef4444; }
.ex-event-main { flex:1; min-width:0; }
.ex-event-main h4 { font-size:12.5px; font-weight:600; margin-bottom:2px; }
.ex-event-main p { font-size:11.5px; color:var(--muted); }
.ex-payload { display:flex; flex-wrap:wrap; gap:5px 6px; margin-top:5px; }
.ex-kv { display:inline-flex; align-items:flex-start; gap:5px; max-width:100%; background:var(--bg); border:1px solid var(--line); border-radius:6px; padding:3px 8px; }
.ex-kv-k { flex-shrink:0; margin-top:1px; font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.03em; color:var(--faint); }
.ex-kv-v { font-size:11px; font-weight:500; color:var(--ink); font-variant-numeric:tabular-nums; white-space:normal; overflow-wrap:anywhere; word-break:break-word; }
.ex-payload-out { align-items:center; }
.ex-out-arrow { color:#16a34a; font-size:13px; font-weight:700; line-height:1; }
.ex-kv-out { background:#f0fdf4; border-color:#bbf7d0; }
.ex-kv-out .ex-kv-k { color:#16a34a; }
.ex-kv-out .ex-kv-v { color:#166534; }
.ex-src { margin-left:7px; font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#5e8e3e; background:#f3f9ee; border:1px solid #d7ecc4; padding:1px 6px; border-radius:999px; vertical-align:middle; }
/* Collapsible tool-call rows (collapsed by default), payload as bullets */
.ex-event-d { position:relative; padding:9px 0; }
.ex-event-sum { display:flex; align-items:flex-start; gap:8px; cursor:pointer; list-style:none; }
.ex-event-sum::-webkit-details-marker { display:none; }
.ex-event-sum::marker { content:""; }
.ex-caret { margin-left:auto; margin-top:1px; flex-shrink:0; color:var(--faint); display:grid; place-items:center; transition:transform .18s ease; }
.ex-event-sum:hover .ex-caret { color:var(--ink); }
details[open] > .ex-event-sum .ex-caret { transform:rotate(90deg); }
.ex-event-body { padding:6px 0 2px; min-width:0; max-width:100%; }
.ex-bullets { margin:0; padding-left:16px; list-style:disc; max-width:100%; }
.ex-bullets li { font-size:11.5px; color:var(--muted); line-height:1.45; overflow-wrap:break-word; word-break:break-word; margin-bottom:3px; }
.ex-bullets li::marker { color:var(--faint); }
.ex-bk { font-weight:700; color:var(--faint); font-size:9px; text-transform:uppercase; letter-spacing:.03em; margin-right:6px; }
.ex-bullets-h { font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#16a34a; margin:9px 0 4px; }
.ex-bullets-out li { color:#166534; }
.ex-bullets-out li::marker { color:#22c55e; }
.ex-bullets-out .ex-bk { color:#16a34a; }
.ex-vpill { font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#16a34a; background:#f0fdf4; padding:3px 8px; border-radius:999px; }
.ex-ms { font-size:11px; color:var(--faint); font-variant-numeric:tabular-nums; }


/* Onboarding tour */
.ex-tour-backdrop { position:fixed; inset:0; background:rgba(15,23,42,.45); z-index:80; display:flex; align-items:center; justify-content:center; animation:exfade .18s ease; }
@keyframes exfade { from{opacity:0;} to{opacity:1;} }
.ex-tour { width:340px; background:var(--card); border-radius:18px; padding:22px; box-shadow:0 30px 70px rgba(16,24,40,.34); animation:expop .2s ease; }
@keyframes expop { from{opacity:0;transform:translateY(8px) scale(.98);} to{opacity:1;transform:none;} }
.ex-tour-step { font-size:10px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; color:var(--acc); margin-bottom:9px; }
.ex-tour h3 { font-size:17px; font-weight:700; letter-spacing:-0.01em; margin-bottom:8px; }
.ex-tour p { font-size:13.5px; color:var(--muted); line-height:1.6; margin-bottom:20px; }
.ex-tour-foot { display:flex; align-items:center; justify-content:space-between; }
.ex-tour-dots { display:flex; gap:6px; }
.ex-tour-dots span { width:6px; height:6px; border-radius:50%; background:var(--line); }
.ex-tour-dots span.on { background:var(--acc); width:18px; border-radius:999px; }
.ex-tour-btns { display:flex; align-items:center; gap:8px; }
.ex-tour-skip { background:none; border:none; color:var(--muted); font-size:13px; font-weight:500; cursor:pointer; padding:8px 10px; }
.ex-tour-next { background:var(--acc); color:#fff; border:none; border-radius:10px; font-size:13px; font-weight:600; padding:9px 18px; cursor:pointer; }
.ex-tour-next:hover { filter:brightness(1.05); }

.ex-close { position:fixed; top:18px; right:18px; z-index:70; width:34px; height:34px; border-radius:50%; border:1px solid var(--line); background:var(--card); color:var(--muted); display:grid; place-items:center; cursor:pointer; }
.ex-close:hover { color:var(--ink); }

/* ── NuPlay console nav (replicated product shell, visual chrome) ── */
.ex-side-top { display:flex; align-items:center; justify-content:space-between; margin-bottom:18px; }
.ex-collapse { color:#5b6580; cursor:pointer; display:grid; place-items:center; background:none; border:none; padding:0; }
.ex-collapse:hover { color:#aab3c9; }
.ex-ws { display:flex; align-items:center; gap:9px; width:100%; background:rgba(255,255,255,.05); border:1px solid rgba(255,255,255,.08); border-radius:11px; padding:8px 10px; margin-bottom:22px; color:#e8ecf6; cursor:pointer; }
.ex-ws-ic { width:26px; height:26px; border-radius:7px; background:linear-gradient(135deg,#1D6BD8,#2582EB); color:#fff; display:grid; place-items:center; flex-shrink:0; }
.ex-ws-name { font-size:13.5px; font-weight:600; flex:1; text-align:left; }
.ex-ws > svg { color:#5b6580; flex-shrink:0; }
.ex-nav { display:flex; flex-direction:column; gap:16px; flex:1; min-height:0; overflow-y:auto; padding-right:2px; }
.ex-nav-group { display:flex; flex-direction:column; gap:1px; }
.ex-nav-label { font-size:10px; font-weight:700; letter-spacing:.12em; text-transform:uppercase; color:#5b6580; margin:0 4px 7px; }
.ex-nav-item { display:flex; align-items:center; gap:11px; padding:8px 11px; border-radius:9px; color:#aab3c9; font-size:13.5px; font-weight:500; cursor:pointer; text-decoration:none; }
.ex-nav-item svg { color:#717c97; flex-shrink:0; }
.ex-nav-item:hover { background:rgba(255,255,255,.035); }
.ex-nav-item.active { background:rgba(255,255,255,.09); color:#fff; }
.ex-nav-item.active svg { color:#fff; }
.ex-brand-av { background:#fff !important; overflow:hidden; }
.ex-brand-av img { width:22px; height:22px; object-fit:contain; }

/* ── Collapsed (icon-only) left rail ── */
.nx-exp.side-collapsed { grid-template-columns:66px minmax(280px,1fr) 360px; }
@media (max-width:1080px){ .nx-exp.side-collapsed { grid-template-columns:66px minmax(240px,1fr) 312px; } }
.side-collapsed .ex-side { padding:22px 11px; align-items:center; }
.side-collapsed .ex-side-top { flex-direction:column; gap:16px; margin-bottom:20px; }
.side-collapsed .ex-logo { height:34px; width:34px; padding:6px; background:#fff; border-radius:9px; filter:none; object-fit:contain; }
.side-collapsed .ex-ws { width:auto; justify-content:center; padding:8px; }
.side-collapsed .ex-ws-name, .side-collapsed .ex-ws > svg { display:none; }
.side-collapsed .ex-nav { align-items:center; }
.side-collapsed .ex-nav-group { align-items:center; width:100%; }
.side-collapsed .ex-nav-label { display:none; }
.side-collapsed .ex-nav-item { justify-content:center; width:42px; padding:9px 0; gap:0; }
.side-collapsed .ex-nav-item span { display:none; }
.side-collapsed .ex-side-foot { justify-content:center; }
.side-collapsed .ex-foot-main { display:none; }

/* ── Capabilities in the Ready stage (light) ── */
.ex-caps { margin-top:30px; width:100%; max-width:340px; }
.ex-caps-list { display:flex; flex-direction:column; gap:12px; }
.ex-caps .ex-cap { display:flex; align-items:flex-start; gap:10px; text-align:left; }
.ex-caps .ex-cap-ic { width:20px; height:20px; border-radius:50%; background:#f0fdf4; color:#16a34a; display:grid; place-items:center; flex-shrink:0; margin-top:1px; }
.ex-caps .ex-cap-main { display:flex; flex-direction:column; gap:1px; }
.ex-caps .ex-cap-t { font-size:13px; font-weight:600; color:var(--ink); }
.ex-caps .ex-cap-d { font-size:11.5px; color:var(--muted); }

/* ═══════════════════════════════════════════════════════════
   RESPONSIVE — the desktop layout is a fixed 3-column grid
   (decorative console sidebar | call stage | customer rail).
   • ≤1024px: drop the decorative sidebar, keep stage + rail 2-up.
   • ≤820px : stack everything into one scrollable column so the
              whole A-to-Z flow works on any phone.
   ═══════════════════════════════════════════════════════════ */
@media (max-width:1024px) and (min-width:821px){
  .nx-exp, .nx-exp.side-collapsed { grid-template-columns:minmax(0,1fr) 340px; }
  .ex-side { display:none; }
}
@media (max-width:820px){
  .nx-exp, .nx-exp.side-collapsed {
    grid-template-columns:1fr;
    display:flex; flex-direction:column;
    overflow-y:auto; overflow-x:hidden;
    -webkit-overflow-scrolling:touch;
  }
  .ex-side { display:none; }               /* decorative console chrome, not core */
  .ex-main { order:1; overflow:visible; padding:16px 14px 20px; }
  .ex-panel { order:2; overflow:visible; border-left:none; border-top:1px solid var(--line); padding:16px 14px 32px; gap:14px; }
  .ex-head { margin-bottom:14px; }
  .ex-head h1 { font-size:19px; }
  .ex-stage { flex:none; min-height:66vh; } /* keep the call area usable, mic reachable */
  .ex-ready { padding:30px 18px; }
  .ex-convo { padding:18px 14px; }
  .ex-bubble { max-width:80vw; }
  .ex-mic { padding:12px 16px 18px; }
  .ex-callsetup, .ex-tryasking, .ex-hints, .ex-caps { max-width:100%; }
  .ex-close { top:12px; right:12px; }
  .ex-tour { width:min(340px,90vw); }
}
@media (max-width:400px){
  .ex-stats { grid-template-columns:repeat(2,1fr); }
  .ex-main { padding:14px 12px 16px; }
  .ex-panel { padding:14px 12px 28px; }
}
`;
